import React, { useEffect, useMemo, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, DragIndicator, Settings, VisibilityOff } from '@mui/icons-material';
import { useAssessmentMutations, useReorder } from '../../../hooks/assessment-hooks';
import {
  arrayMove,
  AssessmentQuestion,
  AssessmentSection,
  uniqueKey,
  VisibleWhen,
} from '../../../support/assessments';
import AssessmentQuestionRow from './AssessmentQuestionRow';
import InlineEditableText from './InlineEditableText';

/**
 * One section in the builder, edited in place — no modal.
 *
 * The card header carries the drag handle and the section title (inline), a settings expander for
 * the description, active flag and conditional rule, and the delete control. Below it, the section's
 * questions are a drag-to-reorder list; "Add Question" appends a new one immediately with an
 * auto-assigned key. Sections and questions are fetched by the builder above and passed down; this
 * card owns only the local drag order and the inline commits.
 */

type Props = {
  section: AssessmentSection;
  questions: AssessmentQuestion[];
  allQuestions: AssessmentQuestion[];
  onDeleteSection: () => void;
};

const AssessmentSectionCard: React.FC<Props> = ({
  section,
  questions,
  allQuestions,
  onDeleteSection,
}) => {
  const sectionMut = useAssessmentMutations('section');
  const questionMut = useAssessmentMutations('question');
  const reorderQuestions = useReorder('question');

  const [settingsOpen, setSettingsOpen] = useState(false);
  // The id of the question just added here, so its row opens expanded for immediate editing.
  const [newQuestionId, setNewQuestionId] = useState<number | null>(null);

  // Local drag order, re-seeded whenever the fetched questions actually change in *content* (not
  // just position). A content-based signature is needed because the rows render several fields
  // straight from the prop (response_type, required, active): keying only on `id:order` meant an
  // edit to any of those refetched with the same id/order, the signature didn't change, the effect
  // never fired, and the row snapped back to the stale value (e.g. the type reverting to "short
  // text"). Identical content still yields an identical signature, so an in-flight drag is not
  // clobbered by a background re-render.
  const [items, setItems] = useState<AssessmentQuestion[]>(questions);
  const signature = useMemo(() => JSON.stringify(questions), [questions]);
  useEffect(() => {
    setItems(questions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Conditional-visibility rule for the whole section, seeded from the row and committed on change.
  const [conditional, setConditional] = useState(false);
  const [condQuestion, setCondQuestion] = useState('');
  const [condMode, setCondMode] = useState<'equals' | 'answered'>('equals');
  const [condValue, setCondValue] = useState('');

  useEffect(() => {
    const vw = section.visible_when as VisibleWhen | null | undefined;
    setConditional(!!vw?.question);
    setCondQuestion(vw?.question ?? '');
    setCondMode(vw?.notEmpty ? 'answered' : 'equals');
    setCondValue(vw?.equals != null ? String(vw.equals) : '');
  }, [section]);

  const conditionSources = useMemo(() => allQuestions.filter((q) => q.key), [allQuestions]);

  // Full-record PUT: the generic runtime rejects a PUT missing any required field (assessment, key,
  // title), so send the whole section with the patch applied rather than only the changed field.
  const commit = (patch: Record<string, any>) =>
    sectionMut.update.mutate({ ...section, ...patch });

  const commitCondition = (
    on: boolean,
    q: string,
    mode: 'equals' | 'answered',
    val: string
  ) => {
    let visible_when: VisibleWhen | null = null;
    if (on && q) visible_when = mode === 'answered' ? { question: q, notEmpty: true } : { question: q, equals: val };
    commit({ visible_when });
  };

  const handleAddQuestion = async () => {
    const created = await questionMut.create.mutateAsync({
      section: section.id,
      key: uniqueKey('question', allQuestions.map((q) => q.key)),
      text: 'New question',
      help_text: '',
      order: items.length,
      response_type: 'text',
      response_options: {},
      visible_when: null,
      weight: null,
      required: false,
      active: true,
    });
    if (created?.id != null) setNewQuestionId(created.id);
  };

  const handleQuestionDrop = (evt: { oldIndex?: number; newIndex?: number }) => {
    const { oldIndex, newIndex } = evt;
    if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    reorderQuestions.mutate(next);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Drag to reorder section">
          <DragIndicator
            className="section-drag-handle"
            sx={{ cursor: 'grab', color: 'text.disabled' }}
          />
        </Tooltip>

        <Box flex={1} minWidth={0}>
          <InlineEditableText
            value={section.title}
            placeholder="Section title"
            variant="title"
            onCommit={(title) => commit({ title })}
          />
        </Box>

        <Chip size="small" label={`${questions.length} question${questions.length === 1 ? '' : 's'}`} />
        {section.active === false && <Chip size="small" label="Inactive" />}
        {section.visible_when && (
          <Tooltip title="Shown conditionally">
            <VisibilityOff fontSize="small" color="action" />
          </Tooltip>
        )}

        <Tooltip title="Section settings">
          <IconButton size="small" onClick={() => setSettingsOpen((v) => !v)}>
            <Settings fontSize="small" color={settingsOpen ? 'primary' : 'inherit'} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete section">
          <IconButton size="small" onClick={onDeleteSection}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={settingsOpen} unmountOnExit>
        <Box display="grid" gap={2} mt={1} px={1}>
          <InlineEditableText
            value={section.description}
            placeholder="Description (optional)"
            multiline
            onCommit={(description) => commit({ description })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={section.active !== false}
                onChange={(e) => commit({ active: e.target.checked })}
              />
            }
            label="Active"
          />
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={conditional}
                  disabled={conditionSources.length === 0}
                  onChange={(e) => {
                    setConditional(e.target.checked);
                    commitCondition(e.target.checked, condQuestion, condMode, condValue);
                  }}
                />
              }
              label="Show only when another answer matches"
            />
            {conditionSources.length === 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                Add questions to this assessment first to make a section conditional.
              </Typography>
            )}
            {conditional && conditionSources.length > 0 && (
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                <TextField
                  select
                  size="small"
                  label="Depends on"
                  value={condQuestion}
                  onChange={(e) => {
                    setCondQuestion(e.target.value);
                    commitCondition(conditional, e.target.value, condMode, condValue);
                  }}
                >
                  {conditionSources.map((q) => (
                    <MenuItem key={q.id} value={q.key}>
                      {q.text || q.key}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Condition"
                  value={condMode}
                  onChange={(e) => {
                    const mode = e.target.value as 'equals' | 'answered';
                    setCondMode(mode);
                    commitCondition(conditional, condQuestion, mode, condValue);
                  }}
                >
                  <MenuItem value="equals">Equals</MenuItem>
                  <MenuItem value="answered">Is answered</MenuItem>
                </TextField>
                {condMode === 'equals' && (
                  <TextField
                    size="small"
                    label="Value"
                    value={condValue}
                    onChange={(e) => setCondValue(e.target.value)}
                    onBlur={() => commitCondition(conditional, condQuestion, condMode, condValue)}
                  />
                )}
              </Box>
            )}
          </Box>
          <Chip size="small" variant="outlined" label={`key: ${section.key}`} sx={{ justifySelf: 'start' }} />
        </Box>
      </Collapse>

      <Divider sx={{ my: 1.5 }} />

      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No questions yet.
        </Typography>
      ) : (
        <ReactSortable
          list={items}
          setList={setItems}
          handle=".question-drag-handle"
          animation={150}
          onEnd={handleQuestionDrop}
        >
          {items.map((question) => (
            <AssessmentQuestionRow
              key={question.id}
              question={question}
              allQuestions={allQuestions}
              defaultExpanded={question.id === newQuestionId}
              onDelete={() => questionMut.remove.mutate(question.id)}
            />
          ))}
        </ReactSortable>
      )}

      <Button size="small" startIcon={<Add />} onClick={handleAddQuestion} disabled={questionMut.create.isPending}>
        Add Question
      </Button>
    </Paper>
  );
};

export default AssessmentSectionCard;
